import { type JSX, useCallback, useEffect, useRef, useState } from 'react';

import { LAUFERGEBNIS, laeufe as holeLaeufe, type Laufzeile } from '../api/status';
import { ApiFehler } from '../api/fehler';
import { Meldung } from '../komponenten/Meldung';
import { genau, vorWieLange } from './zeit';

/**
 * DIE HINTERGRUNDLÄUFE (LOG-09, ERR-10; AP-SQ-14).
 *
 * Ob Absagen, Fristen, Wartelisten und Faktura von selbst laufen, sah bisher
 * niemand. Hier steht je Lauf, wann er zuletzt begann, wie die Runde ausging
 * und wie oft er in Folge gescheitert ist.
 *
 * ÜBERFÄLLIG heißt: Die letzte Runde begann vor mehr als drei Takten. Das ist
 * die Auskunft, die eine gescheiterte Runde nicht gibt — ein Lauf, der gar
 * nicht mehr anfängt, scheitert auch nicht.
 *
 * KEIN EIGENER TAKT, wie die Fehlerablage: `takt` zählt die Abrufe der Seite.
 */

/** Ein Takt in Worten — 60 als „1 min", 86 400 als „24 h". */
function taktText(sekunden: number): string {
  if (sekunden < 60) return `${sekunden} s`;
  if (sekunden < 3600) return `${Math.round(sekunden / 60)} min`;
  return `${Math.round(sekunden / 3600)} h`;
}

export function Hintergrundlaeufe({ takt }: { readonly takt: number }): JSX.Element {
  const [zeilen, setzeZeilen] = useState<readonly Laufzeile[]>([]);
  const [laedt, setzeLaedt] = useState(true);
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);

  const laden = useCallback(async (hintergrund = false): Promise<void> => {
    try {
      setzeZeilen(await holeLaeufe(hintergrund));
      setzeFehler(undefined);
    } catch (f) {
      /* Ein eigener Fehlerweg: Bleibt das Verzeichnis aus, steht der Rest der Seite. */
      if (f instanceof ApiFehler) setzeFehler(f);
    } finally {
      setzeLaedt(false);
    }
  }, []);

  useEffect(() => {
    void laden();
  }, [laden]);

  /* BEN-07: Der Takt der Seite holt im Hintergrund nach, der erste Abruf nicht. */
  const ersterTakt = useRef(takt);
  useEffect(() => {
    if (takt !== ersterTakt.current) void laden(true);
  }, [takt, laden]);

  const ueberfaellig = zeilen.filter((z) => z.ueberfaellig).length;
  const gestoert = zeilen.filter((z) => z.stoerung !== null).length;

  return (
    <div className="karte">
      <div className="karte__titel">
        <h2>Hintergrundläufe</h2>
        {ueberfaellig > 0 && (
          <span className="marke marke--krit">{ueberfaellig} überfällig</span>
        )}
        {gestoert > 0 && <span className="marke marke--krit">{gestoert} gestört</span>}
      </div>
      {fehler !== undefined && <Meldung fehler={fehler} />}
      <p className="hilfe">
        Die automatischen Läufe. Überfällig ist ein Lauf, dessen letzte Runde vor mehr als drei Takten
        begann. Nach drei Fehlschlägen in Folge steht die Störung in der Fehlerablage.
      </p>
      <div className="tabellenhuelle tabellenhuelle--telefon">
        <table className="liste__tabelle">
          <caption className="nur-vorlesen">Zustand der Hintergrundläufe</caption>
          <thead>
            <tr>
              <th scope="col">Lauf</th>
              <th scope="col">Takt</th>
              <th scope="col">Letzte Runde</th>
              <th scope="col">Ergebnis</th>
              <th scope="col">Fehler in Folge</th>
            </tr>
          </thead>
          <tbody>
            {laedt && (
              <tr>
                <td colSpan={5}>Läufe werden geladen …</td>
              </tr>
            )}
            {!laedt && zeilen.length === 0 && (
              <tr>
                <td colSpan={5}>Keine Hintergrundläufe verzeichnet.</td>
              </tr>
            )}
            {zeilen.map((z) => (
              <tr key={z.name} data-ueberfaellig={z.ueberfaellig ? 'ja' : undefined}>
                <th scope="row">
                  {z.bezeichnung}
                  {z.ueberfaellig && (
                    <>
                      {' '}
                      {/* AP-SQ-14 */}
                      <span className="marke marke--krit">überfällig</span>
                    </>
                  )}
                  <div className="feld__hilfe">
                    <code>{z.name}</code>
                  </div>
                </th>
                <td className="zahl">{taktText(z.taktSekunden)}</td>
                <td className="zahl" title={genau(z.letzterBeginn)}>
                  {vorWieLange(z.letzterBeginn)}
                </td>
                <td>
                  {z.letztesErgebnis === null ? (
                    <span className="marke marke--inaktiv">noch keine Runde</span>
                  ) : (
                    <span className={`marke marke--${LAUFERGEBNIS[z.letztesErgebnis].marke}`}>
                      {LAUFERGEBNIS[z.letztesErgebnis].name}
                    </span>
                  )}
                  {z.letzteMeldung !== null && <div className="hilfe">{z.letzteMeldung}</div>}
                </td>
                <td className="zahl">{z.fehlerFolge}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
